// `grid scaffold` namespace. Per decision 015 §"Scaffold commands"
// and backlog 073-D:
//
//   grid scaffold                            (idempotent re-scan)
//   grid scaffold rename <old> <new>         (atomic rename)
//   grid scaffold env-example                (auto-generate .env.example)
//
// BL621 Phase 3 (Decision 042 §"Renames") — `scaffold service <name>` moved to
// `init service <name>` (registered under the `init` tree). The shared
// `runScaffoldService` implementation in ./service.ts is unchanged.

import { Command } from 'commander';
import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { parse as parseYaml } from 'yaml';
import { CloudGridError } from '../../utils.js';
import { handleV2Error } from '../../handle-error.js';
import { scaffoldRenameCommand } from './rename.js';
import { scaffoldEnvExampleCommand } from './env-example.js';
import { scaffoldService } from './scaffolder.js';
import type { ServiceType } from './templates.js';
import { isHttpModeCron } from '../../services/cron-mode.js';

interface CloudGridYamlShape {
  services?: Record<string, { type: string; run?: string }>;
}

/**
 * Top-level `grid scaffold`. Walks every service declared in
 * `cloudgrid.yaml` and idempotently writes any per-type starter files that
 * are missing on disk. Files that already exist are NEVER touched —
 * `scaffoldService` consults the filesystem and skips them. Tests verify
 * this directly.
 */
export async function runScaffoldRescan(opts: { cwd?: string } = {}): Promise<void> {
  const cwd = opts.cwd ?? process.cwd();
  const yamlPath = join(cwd, 'cloudgrid.yaml');
  if (!existsSync(yamlPath)) {
    throw new CloudGridError('No cloudgrid.yaml found. Run: grid init <kind> <name>');
  }
  let parsed: CloudGridYamlShape;
  try {
    parsed = parseYaml(readFileSync(yamlPath, 'utf-8')) as CloudGridYamlShape;
  } catch (err) {
    throw new CloudGridError(
      `Could not parse cloudgrid.yaml: ${(err as Error).message}`,
    );
  }
  const services = parsed?.services ?? {};
  if (Object.keys(services).length === 0) {
    process.stdout.write('No services declared in cloudgrid.yaml.\n');
    return;
  }
  process.stdout.write('Scaffolding declared services...\n');
  for (const [name, svc] of Object.entries(services)) {
    const type = svc.type as ServiceType;
    // Backlog 076-B: HTTP-mode cron is sourceless — the platform calls a
    // remote URL on schedule, no folder, no stub, no .env.example.
    if (isHttpModeCron(svc)) {
      process.stdout.write(`  ${name} (cron): HTTP mode — no folder needed (run: ${svc.run})\n`);
      continue;
    }
    const result = scaffoldService(cwd, name, type);
    if (result.written.length === 0 && result.skipped.length > 0) {
      process.stdout.write(`  ${name} (${type}): up to date (${result.skipped.length} files preserved)\n`);
      continue;
    }
    process.stdout.write(`  ${name} (${type}):\n`);
    for (const f of result.written) {
      process.stdout.write(`    + ${f}\n`);
    }
    for (const f of result.skipped) {
      process.stdout.write(`    = ${f} (preserved)\n`);
    }
  }
}

export const scaffoldCommand = new Command('scaffold')
  .description('Scaffold service folders declared in cloudgrid.yaml (idempotent)')
  .action(async () => {
    try {
      await runScaffoldRescan();
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  })
  .addCommand(scaffoldRenameCommand)
  .addCommand(scaffoldEnvExampleCommand);
